import React, { ReactElement, useEffect } from 'react';

import { AnimatePresence, m as motion, useAnimation } from 'framer-motion';
import styled from 'styled-components';
import { ClaimFlowType } from 'types';

import {
  AltButton,
  StyledButton,
  UnderlinedBoldGhostButton,
} from 'chime-ui/components/Button';
import { useErrorDialog } from 'chime-ui/components/Modal';
import { Superscript, Text, Title } from 'chime-ui/components/Typography';
import { colors } from 'chime-ui/system';

import { createNudataScript } from 'components/SubPages/ClaimInfo/utils/nudata/createNudataScript';

import { useBonusAmount } from 'hooks/useBonusAmount';
import useP2PEnrollmentFlow from 'hooks/useP2PEnrollmentFlow';
import { useUserContext } from 'hooks/useUserContext';

import LandingBase from '../LandingBase';
import PayAnyoneValueProps from '../Pay/PayAnyoneValueProps';
import { landingVariants } from './ReturnUseLanding.animation';
import useReturnUserLanding from './useReturnUserLanding';
import WelcomeMessage from './WelcomeMessage';

function ReturnUserLanding(): ReactElement {
  const [userData] = useUserContext();
  const {
    name,
    amount,
    claimWithChime: isClaimWithChimeEnabled,
    senderId,
    previousClaim,
  } = userData;
  const { bonusAmount } = useBonusAmount();
  const {
    onChangeCard,
    onCtaClickReuseDebit,
    onCtaClickChime,
    isLoading,
    errorMessage,
    dismissError,
  } = useReturnUserLanding(amount, senderId);

  const {
    Modal: ErrorDialog,
    isShowing: errorDialogIsShowing,
    handleShow: showErrorDialog,
  } = useErrorDialog({
    onDismiss: () => dismissError(),
  });

  useEffect(() => {
    if (errorMessage) {
      showErrorDialog();
    }
  }, [errorMessage, showErrorDialog]);

  useEffect(() => {
    createNudataScript();
  }, []);

  const { activateP2pEnrollmentAndNavigate } = useP2PEnrollmentFlow(
    isClaimWithChimeEnabled,
    amount
  );

  const animationControls = useAnimation();
  useEffect(() => {
    const startAnimation = async () => {
      await animationControls.start('welcomeEnter');
      await animationControls.start('welcomeExit');
      await animationControls.start('landingEnter');
    };

    startAnimation();
  }, [animationControls]);

  return (
    <>
      <WelcomeMessage
        animationControls={animationControls}
        firstName={previousClaim.firstName}
      />
      <motion.div animate={animationControls} variants={landingVariants}>
        <LandingBase
          pageLocation={'Return User Page'}
          claimFlowType={ClaimFlowType.pay}
          landingTitle={
            <>
              <Title size={'large'}>{name}</Title>
              <Title size={'large'}>sent you ${amount}</Title>
            </>
          }
        >
          <GreetingLayout>
            <Text size={'small'} bold>
              <span role={'img'} aria-label={'lightning bolt'}>
                🎉
              </span>{' '}
              It’s good to see you again
            </Text>
          </GreetingLayout>
          <CTALayout>
            <PrimaryButton
              onClick={() => activateP2pEnrollmentAndNavigate(onCtaClickChime)}
            >
              Cash out with Chime
              {isClaimWithChimeEnabled && ` + $${bonusAmount} bonus`}
              <Superscript>†</Superscript>
            </PrimaryButton>

            {/* NOTE: nudata script (nds) needs this form tag to inject its input tag */}
            <form onSubmit={e => e.preventDefault()}>
              <SecondaryButton
                disabled={isLoading}
                onClick={() =>
                  onCtaClickReuseDebit(
                    previousClaim.lastFour,
                    previousClaim.externalCardId
                  )
                }
              >
                Cash out to card ending in {previousClaim.lastFour}
              </SecondaryButton>
            </form>
          </CTALayout>
          <ChangeCardLayout>
            <Text size={'small'} color={colors.darkGrey}>
              Not {previousClaim.firstName}? Need to{' '}
              <UnderlinedBoldGhostButton onClick={onChangeCard}>
                change debit card?
              </UnderlinedBoldGhostButton>
            </Text>
          </ChangeCardLayout>
          <PayAnyoneValueProps />
        </LandingBase>

        <AnimatePresence>
          {errorDialogIsShowing && <ErrorDialog>{errorMessage}</ErrorDialog>}
        </AnimatePresence>
      </motion.div>
    </>
  );
}

const GreetingLayout = styled.div`
  text-align: center;
  margin-bottom: 0.5rem;
`;

const CTALayout = styled.div`
  display: grid;
  row-gap: 1rem;
`;

const ChangeCardLayout = styled.div`
  padding-top: 1rem;
  text-align: center;
`;

const SecondaryButton = styled(AltButton)`
  color: ${colors.darkGreen};
`;

const PrimaryButton = styled(StyledButton)`
  box-shadow: 0 4px 12px rgba(51, 51, 51, 0.16);
`;

export default ReturnUserLanding;
